import { defineStore } from "pinia";
import { ref } from "vue";
import {
    Lloyds3DSMethodDataResponse,
    LloydsChallengeDataResponse,
} from "@/helpers/interfaces/providers";

/**
 * Lloyds store to manage Lloyds payment-related data such as 3DS method and challenge data.
 * The store persists data in sessionStorage to maintain state across page
 * reloads within the same session.
 */
export const useLloydsStore = defineStore(
    "lloyds",
    () => {
        // ── State ──────────────────────────────────────────────────────────────
        const loadLloydsScript = ref(false);
        const lloyds3DSMethodData = ref<Lloyds3DSMethodDataResponse | null>(
            null,
        );
        const lloydsChallengeData = ref<LloydsChallengeDataResponse | null>(
            null,
        );
        const lloyds3DSMethodDataConsumed = ref(false);
        const lloydsChallengeDataConsumed = ref(false);
        const isPaymentActionInProgress = ref(false);

        // ── Actions ────────────────────────────────────────────────────────────
        function setLoadLloydsScript(load: boolean) {
            loadLloydsScript.value = load;
        }

        function setLloyds3DSMethodData(
            methodData: Lloyds3DSMethodDataResponse | null,
        ) {
            lloyds3DSMethodData.value = methodData;
        }

        function setLloydsChallengeData(
            challengeData: LloydsChallengeDataResponse | null,
        ) {
            lloydsChallengeData.value = challengeData;
        }

        function setLloyds3DSMethodDataConsumed(consumed: boolean) {
            lloyds3DSMethodDataConsumed.value = consumed;
        }

        function setLloydsChallengeDataConsumed(consumed: boolean) {
            lloydsChallengeDataConsumed.value = consumed;
        }

        function setIsPaymentActionInProgress(value: boolean) {
            isPaymentActionInProgress.value = value;
        }

        return {
            loadLloydsScript,
            lloyds3DSMethodData,
            lloydsChallengeData,
            lloyds3DSMethodDataConsumed,
            lloydsChallengeDataConsumed,
            setLoadLloydsScript,
            setLloyds3DSMethodData,
            setLloydsChallengeData,
            setLloyds3DSMethodDataConsumed,
            setLloydsChallengeDataConsumed,
            isPaymentActionInProgress,
            setIsPaymentActionInProgress,
        };
    },
    {
        persist: {
            pick: [
                "loadLloydsScript",
                "lloyds3DSMethodData",
                "lloydsChallengeData",
                "lloyds3DSMethodDataConsumed",
                "lloydsChallengeDataConsumed",
            ],
            storage: sessionStorage,
        },
    },
);
